// Simple in-memory cache for POST /methods responses.
//
// Contributions is the heaviest screen the app can show. Its
// only API call — POST /methods — takes ~500-700 ms server-side
// because the engine layer runs 4 methods × 30-year projections
// through a bisection solver, then serializes to a 42 KB payload.
//
// Users almost always look at Dashboard for a moment before
// clicking Contributions. We use that idle window to fire the
// same POST /methods request silently in the background. When
// Contributions mounts, it checks this cache first — if a fresh
// hit exists, the screen paints in a single frame instead of
// waiting ~700 ms for a fresh request.
//
// Cache key = `${aid}::${sortedJSON(variables)}`. Any change to
// the association or the scenario variables produces a new key,
// so the cache is naturally invalidated by user intent.
//
// TTL = 60 s. Past that we re-fetch — variables the user hasn't
// touched but the backend has been re-seeded / migrated / etc.
// shouldn't linger indefinitely.

import axios from "axios";

const TTL_MS = 60_000;
const store = new Map();

function makeKey(aid, variables) {
  if (!aid || !variables) return null;
  // Stable key: sorted variable entries.
  const sorted = Object.keys(variables)
    .sort()
    .reduce((acc, k) => {
      acc[k] = variables[k];
      return acc;
    }, {});
  return `${aid}::${JSON.stringify(sorted)}`;
}

export function getCachedMethods(aid, variables) {
  const key = makeKey(aid, variables);
  if (!key) return null;
  const entry = store.get(key);
  if (!entry) return null;
  if (Date.now() - entry.at > TTL_MS) {
    store.delete(key);
    return null;
  }
  return entry.data;
}

export function setCachedMethods(aid, variables, data) {
  const key = makeKey(aid, variables);
  if (!key) return;
  store.set(key, { at: Date.now(), data });
}

// Fire the /methods request in the background and cache its
// response. Silent on failure — if the preload dies, Contributions
// will just fire its own request on mount.
export function preloadMethods(apiBase, aid, variables) {
  const key = makeKey(aid, variables);
  if (!key) return;
  // Skip if a fresh entry already exists — avoids duplicate work
  // when the user leaves and returns to a screen quickly.
  if (getCachedMethods(aid, variables)) return;
  axios
    .post(`${apiBase}/associations/${aid}/methods`, { variables })
    .then((res) => setCachedMethods(aid, variables, res.data.methods))
    .catch(() => { /* preload failure is silent by design */ });
}

// Called on logout / tenant switch — flush every entry so cross-
// account leakage is impossible.
export function clearMethodsCache() {
  store.clear();
}
